import { captureException } from "@sentry/react";
import type { QueryClient } from "@tanstack/react-query";
import i18n from "i18next";
import { toast } from "sonner";
import { ActivityKeys } from "web_myplann/Api/Activities/ActivityKeys";
import { MiscellaneousKeys } from "web_myplann/Api/Miscellaneous/miscKeys";
import { NotificationsKeys } from "web_myplann/Api/Notifications/notificatioKeys";
import {
	patchNotificationsIdReadV2,
	postNsureQuestionnaireAccessV2,
} from "web_myplann/Infrastructure/Api/Api";
import { handleCall } from "web_myplann/Infrastructure/Api/ApiUtils";
import { setBridgedOnlineStatus } from "web_myplann/Infrastructure/Hooks/onlineStatusStore";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import type { ColorSchemeOptions } from "web_myplann/Infrastructure/Themes/ColorSchemeProvider";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import { AppRouting } from "web_myplann/Infrastructure/Utils/UrlUtils";
import { device, NavigationTypes } from "web_myplann/Utils/device";
import {
	NavigationDestinationType,
	type RpcMessage,
	type WebViewMessage,
	WebViewMessageTypes,
} from "web_myplann/Utils/WebViewModel";

declare global {
	interface Window {
		ReactNativeWebView?: {
			postMessage(message: string): void;
		};
	}
}

const handledNavigationMessageIds = new Set<string>();

export const handleIncomingMessage = async (
	message: WebViewMessage,
	setMode: (variant: Partial<ColorSchemeOptions>) => void,
	queryClient: QueryClient,
	navigate: (path: string) => void,
) => {
	if (message.type === WebViewMessageTypes.SWITCH_LANGUAGE) {
		i18n.changeLanguage(message.payload.language);
	}

	if (message.type === WebViewMessageTypes.SWITCH_DARK_MODE) {
		setMode({ paletteMode: message.payload.darkMode ? "dark" : "light" });
	}

	if (message.type === WebViewMessageTypes.CONNECTIVITY_STATUS) {
		setBridgedOnlineStatus(message.payload.isOnline);
	}

	if (message.type === WebViewMessageTypes.REFRESH_NOTIFICATIONS_REQUEST) {
		queryClient.invalidateQueries({
			queryKey: NotificationsKeys.count(),
		});
		queryClient.invalidateQueries({
			queryKey: [...ActivityKeys.all, "list"],
		});
	}

	if (message.type === WebViewMessageTypes.REFRESH_APP_INFO_REQUEST) {
		queryClient.refetchQueries({
			queryKey: MiscellaneousKeys.appColor(),
		});
	}

	if (message.type === WebViewMessageTypes.NAVIGATION_REQUEST) {
		const { url } = message.payload;

		switch (url) {
			case NavigationTypes.WebDashboard:
				navigate(Routing.Dashboard.path());
				break;
			case NavigationTypes.WebPortfolio:
				navigate(Routing.Portfolio.PortfolioTabs.path());
				break;
			case NavigationTypes.WebActivity:
				navigate(Routing.ActivitiesList.path());
				break;
			case NavigationTypes.GoalsScreen:
				navigate(Routing.Goals.GoalsList.path());
				break;
			case NavigationTypes.MoreScreen:
				navigate(Routing.Profile.More.path());
				break;
		}
	}

	if (message.type === WebViewMessageTypes.NAVIGATE_NOTIFICATION_REQUEST) {
		const { messageId } = message as Partial<RpcMessage<WebViewMessage>>;
		if (messageId) {
			if (handledNavigationMessageIds.has(messageId)) {
				return;
			}
			handledNavigationMessageIds.add(messageId);
			window.ReactNativeWebView?.postMessage(
				JSON.stringify({
					type: WebViewMessageTypes.NAVIGATE_NOTIFICATION_RESPONSE,
					payload: { received: true },
					messageId,
				}),
			);
		}

		const { sourceId, type, id, clientType, identificationNumber } =
			message.payload;

		const result = await handleCall(patchNotificationsIdReadV2(id));

		if (result.isSuccess) {
			queryClient.invalidateQueries({
				queryKey: NotificationsKeys.count(),
			});
			queryClient.invalidateQueries({
				queryKey: NotificationsKeys.infiniteGroupedList(true),
			});
			queryClient.invalidateQueries({
				queryKey: NotificationsKeys.infiniteGroupedList(false),
			});
		}

		if (type === NavigationDestinationType.activity) {
			navigate(AppRouting.MyPlann.ActivityDetail.path(sourceId));
		} else if (type === NavigationDestinationType.task) {
			navigate(AppRouting.MyPlann.TaskDetail.path(sourceId));
		} else if (
			type === NavigationDestinationType.investmentQuestionnaire &&
			clientType &&
			identificationNumber
		) {
			void device.showProcessingOverlay(true);
			try {
				const access = await handleCall(
					postNsureQuestionnaireAccessV2({ identificationNumber, clientType }),
				);
				if (!access.isSuccess) {
					return;
				}
				const domain = new URL(access.data.url).hostname;
				const opened = await device.openExternalUrl(access.data.url, [
					{
						name: "clientZoneExtToken",
						value: access.data.accessToken,
						domain,
						path: "/",
						secure: true,
					},
				]);
				if (!opened.payload.success) {
					toast.error(i18n.t(Resources.Common.somethingWentWrong));
				}
			} catch (error) {
				captureException(error);
				toast.error(i18n.t(Resources.Common.somethingWentWrong));
			} finally {
				void device.showProcessingOverlay(false);
			}
		}
	}
};
